import { Box, styled, Typography } from '@mui/material';
import Avatar from 'further-ui/components/Avatar';
import Button from 'components/Button';
import { Column, Row } from 'components/Layout';
import PageFilters from 'components/new/PageFilters';
import SearchField from 'further-ui/components/SearchField';
import { useLayoutContext } from 'contexts/LayoutContext';
import BasicSelect from 'further-ui/components/BasicSelect';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { usePaginatedFilters } from 'further-ui/hooks';
import {
  dateToLabelAltFormat,
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { revaluation as rules } from 'further-ui/validations';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import CompanyRevaluationRow from './CompanyRevaluationRow';
import useBulkRevaluationsSort from './useBulkRevaluationsSort';
import BulkReviewRevaluations from './BulkReviewRevaluations';
import FullPageDrawer from 'components/new/FullPageDrawer';
import BulkRevaluationsDrawerTitle from './BulkRevaluationsDrawerTitle';

const FilterContainer = styled(Box)({
  display: 'flex',
  width: '350px',
  flexDirection: 'column',
  alignItems: 'stretch',
});

enum Steps {
  Create = 0,
  Review = 1,
}

type RevaluationTab = 'total' | 'perClass';

const pageId = 'bulk-revaluations';

type Props = {
  onExit: () => void;
};

const BulkRevaluations: React.FC<Props> = ({ onExit }) => {
  const { vehicle } = useLayoutContext();
  const [activeStep, setActiveStep] = useState<Steps>(Steps.Create);
  const { periodId } = useParams<{ periodId?: string }>();

  const handleMutation = useHandleTrpcMutation();

  const confirmChecklistItems =
    api.accountingPeriod.confirmChecklistItems.useMutation(
      handleMutation({
        onSuccess: () => {
          onExit();
        },
      }),
    );

  const handleMarkAsComplete = () => {
    if (!vehicle?._id || !periodId) return;
    confirmChecklistItems.mutate({
      vehicleId: vehicle._id,
      id: periodId,
      items: ['companyValuations'],
      confirmed: true,
    });
  };

  const { pagination, filters, handleChangeFilters } = usePaginatedFilters<
    { search: string },
    'name'
  >(pageId, {
    defaultFilters: {
      search: '',
    },
    defaultOrder: {
      orderBy: 'name',
      order: 'asc',
    },
  });

  const { sortSelectOptions, sortSelectValue, handleChangeSort } =
    useBulkRevaluationsSort(pagination);

  const { data: periodData } = api.accountingPeriod.getPeriodById.useQuery(
    { vehicleId: vehicle?._id ?? '', id: periodId ?? '' },
    { enabled: !!periodId && !!vehicle?._id },
  );

  const periodEndDate = periodData?.periodEnd?.toUTCDate();

  const { data, isFetching } = api.lpCompany.list.useQuery({
    vehicleId: vehicle?._id ?? '',
    ...pagination,
    searchQuery: filters.search,
    periodEndDate: periodData?.periodEnd?.toString(),
  });

  const companyRows = (data?.results ?? []).map((company) => ({
    id: company._id,
    ...company,
  }));

  const [expandedCompanyIds, setExpandedCompanyIds] = useState<Set<string>>(
    new Set(),
  );

  const [collectedData, setCollectedData] = useState<
    Record<
      string,
      {
        formData: rules.RevaluationFormType;
        assetId: string;
        assetName: string;
        name: string;
        companyLogo?: string | null;
      }
    >
  >({});
  const [selectedTabsByCompanyId, setSelectedTabsByCompanyId] = useState<
    Record<string, RevaluationTab>
  >({});

  const handleDataChange = (
    companyId: string,
    formData: rules.RevaluationFormType,
    assetId: string,
    assetName: string,
    name: string,
    companyLogo?: string | null,
  ) => {
    setCollectedData((prev) => ({
      ...prev,
      [companyId]: { formData, assetId, assetName, name, companyLogo },
    }));
  };

  const handleRevaluationTabChange = (
    companyId: string,
    tab: RevaluationTab,
  ) => {
    setSelectedTabsByCompanyId((current) => ({
      ...current,
      [companyId]: tab,
    }));
  };

  type CompanyRow = (typeof companyRows)[number];

  const columns: Array<TableColumn<CompanyRow>> = [
    {
      label: 'Asset',
      key: 'name',
      render: (company) => (
        <Row centered spacing="xs">
          <Avatar
            src={company.companyLogo ?? undefined}
            text={company.name}
            size="lg"
            color="random"
          />
          <div>{company.name}</div>
        </Row>
      ),
    },
    {
      label: 'Total hold value',
      key: 'currentHoldValueLcy',
      disableSorting: true,
      render: (company) =>
        numberToCurrencyString(company.currentHoldValueLcy, {
          currency: vehicle?.baseCurrency,
        }),
    },
    {
      label: 'Average unit price',
      key: 'currentNumberOfUnits',
      disableSorting: true,
      render: (company) => {
        if (!company.currentNumberOfUnits) return '--';
        return numberToCurrencyString(
          company.currentHoldValueLcy / company.currentNumberOfUnits,
          { currency: vehicle?.baseCurrency },
        );
      },
    },
    {
      label: 'Units held',
      key: 'totalNumberOfUnits',
      disableSorting: true,
      render: (company) => numberToDisplayString(company.currentNumberOfUnits),
    },
    {
      label: 'Revaluation date',
      key: 'latestRevaluationDate',
      disableSorting: true,
      render: (company) => dateToLabelAltFormat(company.latestRevaluationDate),
    },
  ];

  const hasCollectedData = Object.keys(collectedData).length > 0;

  if (activeStep === Steps.Review && vehicle) {
    return (
      <BulkReviewRevaluations
        collectedData={collectedData}
        vehicle={vehicle}
        onBack={() => setActiveStep(Steps.Create)}
        onExit={onExit}
        onSuccess={() => {
          setActiveStep(Steps.Create);
          setExpandedCompanyIds(new Set());
          setCollectedData({});
          setSelectedTabsByCompanyId({});
        }}
      />
    );
  }

  return (
    <FullPageDrawer
      title={
        <BulkRevaluationsDrawerTitle
          title="Asset revaluations"
          activeStep={activeStep}
          onClose={onExit}
        />
      }
      onClose={onExit}
      innerScroll
      open
      size="lg"
      actions={
        <Row fullWidth justify="space-between">
          <Button
            variant="outlined"
            size="large"
            color="primary"
            onClick={onExit}
          >
            Exit
          </Button>
          <Box sx={{ flex: 1 }} />
          <Row spacing="md">
            {periodId && (
              <Button
                size="large"
                variant="outlined"
                color="primary"
                onClick={handleMarkAsComplete}
                loading={confirmChecklistItems.isPending}
              >
                Mark as complete
              </Button>
            )}
            <Button
              size="large"
              variant="contained"
              color="primary"
              disabled={!hasCollectedData}
              onClick={() => setActiveStep(Steps.Review)}
            >
              Continue
            </Button>
          </Row>
        </Row>
      }
    >
      <Column fullWidth spacing="md">
        <Column spacing="lg">
          <Column>
            <Typography variant="h5">Review asset valuations</Typography>
            <Typography variant="body2">
              Review and update asset valuations.
            </Typography>
          </Column>
          <PageFilters justify="space-between">
            <FilterContainer>
              <SearchField
                name="search"
                placeholder="Search by company"
                value={filters.search}
                fullWidth
                onSearch={(search) => {
                  handleChangeFilters({ search });
                }}
                allowEmptyQuery
                autoSearchOnDebounce
              />
            </FilterContainer>
            <FilterContainer>
              <BasicSelect
                fullWidth
                value={sortSelectValue}
                onChange={(value) => {
                  handleChangeSort(value);
                }}
                options={sortSelectOptions}
                placeholder="Sort by"
              />
            </FilterContainer>
          </PageFilters>
        </Column>
        <Table
          columns={columns}
          rows={companyRows}
          isLoading={isFetching}
          rowsTotalCount={data?.totalCount ?? 0}
          pagination={pagination}
          isExpandableRow={() => true}
          isExpandedRow={(row) => expandedCompanyIds.has(row._id)}
          onRowExpand={(row) => {
            setExpandedCompanyIds((current) => {
              const next = new Set(current);
              if (next.has(row._id)) {
                next.delete(row._id);
              } else {
                next.add(row._id);
              }
              return next;
            });
          }}
          darkExpandedContent
          getExpandedContent={(row) =>
            vehicle ? (
              <CompanyRevaluationRow
                company={row}
                vehicleId={vehicle._id}
                vehicle={vehicle}
                savedData={collectedData[row._id]?.formData}
                periodEndDate={periodEndDate}
                initialTab={selectedTabsByCompanyId[row._id] ?? 'total'}
                onTabChange={handleRevaluationTabChange}
                onDataChange={handleDataChange}
              />
            ) : null
          }
          emptyMessage="No companies found"
        />
      </Column>
    </FullPageDrawer>
  );
};

export default BulkRevaluations;
